import { T, useT } from '@transifex/react';
import { Heading, HStack, Text, VStack } from '@vidahealth/vida-ui';
import { View } from 'react-native';

import CheckIcon from '@/assets/icons/check-icon.svg';
import { BottomSheet, CtaButton } from '@/modules/ui';

type MismatchExamplesSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * in-year-revenue test arm. The sheet auto-opens there, stacking a second
   * failure statement on EmployeeSearchScreen's own heading, so the copy leads
   * with the remedy instead. Control is only ever reached by tapping the link,
   * where restating the failure still reads correctly.
   */
  leadsWithRemedy?: boolean;
};

// Bottom-sheet equivalent of stylos's MismatchExamplesDrawer. Four hardcoded
// scenarios explaining the most common reasons an eligibility lookup fails,
// with a CTA that just dismisses the sheet so the user can edit the form
// behind it.
export function MismatchExamplesSheet({
  isOpen,
  onClose,
  leadsWithRemedy = false,
}: MismatchExamplesSheetProps) {
  const t = useT();

  const scenarios: { id: string; title: string; description: string }[] = [
    {
      id: 'nicknames',
      title: t('Nicknames or preferred names:'),
      description: t(
        'Enter your full name and avoid using nicknames (e.g. use Robert instead of Bob).'
      ),
    },
    {
      id: 'middle-initials',
      title: t('Middle initials:'),
      description: t(
        "Add your middle initial if it's on file with your organization (e.g. Sarah K. instead of Sarah)."
      ),
    },
    {
      id: 'hyphenated-names',
      title: t('Hyphenated last names:'),
      description: t('Include your full last name (e.g. Gonzalez-Santos instead of Gonzalez).'),
    },
    {
      id: 'name-changes',
      title: t('Recent name changes:'),
      description: t(
        'Use the name previously on file with your organization (e.g. Thompson instead of Carter).'
      ),
    },
  ];

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <CtaButton onPress={onClose} testID='mismatch-examples-update-details'>
          <T _str='Update Details' />
        </CtaButton>
      }
    >
      <VStack space='xl'>
        <Heading size='xl' className='font-heading-semibold text-primary-800 dark:text-primary-0'>
          {leadsWithRemedy ? (
            <T _str='Common reasons this happens' />
          ) : (
            <T _str='Common mismatches' />
          )}
        </Heading>

        <Text size='md'>
          {leadsWithRemedy ? (
            <T _str='Make sure your name and date of birth match what your organization has on file. A few common scenarios that can prevent a match:' />
          ) : (
            <T _str='Make sure your name and date of birth match what your organization has on file. Here are a few common scenarios that can prevent us from confirming your eligibility:' />
          )}
        </Text>

        <VStack space='lg'>
          {scenarios.map((scenario) => (
            <HStack key={scenario.id} space='md' className='items-start'>
              <View className='pt-1'>
                <CheckIcon width={24} height={24} />
              </View>
              <Text size='md' className='flex-1'>
                <Text className='font-inter-600'>{scenario.title}</Text> {scenario.description}
              </Text>
            </HStack>
          ))}
        </VStack>
      </VStack>
    </BottomSheet>
  );
}
